import { bookSightWords, holds, itemKey, pictureSrc, READING_PATH, WORDS, type ItemMemory, type StepStanding } from '@aurora/core'
import { TodayCards } from '../cards/TodayCards'
import type { useCards } from '../cards/useCards'
import { playStep } from '../../lib/games'
import { play } from '../../lib/feedback'
import { BlendLadder, Cover } from './ReadingPath'

/**
 * Today, for her: the two things to hand her, side by side. Her cards (every
 * deck in one session) and the next step on her reading path, each one tap
 * from playing. Each button names what it plays, and only one is filled:
 * cards she stopped partway through come first, since finishing them is
 * what today's spacing counts on; otherwise the step on her path leads.
 */
export function TodayHero({ cards, next, childId, onNewDeck }: {
  cards: ReturnType<typeof useCards>
  next: StepStanding | null
  childId: string
  onNewDeck: () => void
}) {
  const cardsLead = !next || (!!cards.session && cards.partway && !cards.doneToday)
  return (
    <section className="play-today" aria-label="Today">
      <TodayCards cards={cards} onNewDeck={onNewDeck} compact lead={cardsLead} />
      <NextStep next={next} childId={childId} name={cards.child?.name ?? 'her'} lead={!cardsLead} memory={cards.memory} now={cards.now} />
    </section>
  )
}

function NextStep({ next, childId, name, lead, memory, now }: {
  next: StepStanding | null; childId: string; name: string; lead: boolean; memory: Record<string, ItemMemory>; now: number
}) {
  if (!next) {
    return (
      <section className="next-step is-empty reveal" style={{ '--i': 1 } as React.CSSProperties}>
        <p className="today-cards-kicker">Her reading path</p>
        <h2 className="today-cards-title">Every step is done</h2>
        <p className="today-cards-body">Anything on the path below plays again whenever {name} wants it.</p>
      </section>
    )
  }
  const { step } = next
  const sounds = step.track === 'sounds'
  const blend = step.track === 'blend'
  // A sounds set and Blending show their own pictures in tiles across the card; anything else wears its cover.
  const tiled = sounds || blend
  return (
    <section className={`next-step reveal${tiled ? ' is-tiled' : ''}`} style={{ '--i': 1 } as React.CSSProperties}>
      {!tiled && (
        <div className="next-step-art">
          <Cover word={step.cover} size={112} />
        </div>
      )}
      <p className="today-cards-kicker">Next on her reading path</p>
      <h2 className="today-cards-title">{heroTitle(step)}</h2>
      {blend && <BlendInside next={next} memory={memory} now={now} />}
      {sounds ? <PictureTiles label={words(step).join(', ')} tiles={words(step).map((l) => ({ key: l, word: starter(l), big: l, small: starter(l)?.word }))} /> : SETS.has(step.track) && (
        <p className="next-step-words" aria-label={words(step).join(', ')}>
          {words(step).map((w) => <span key={w}>{w}</span>)}
        </p>
      )}
      {step.track === 'books' && <BookInside config={step.config} />}
      <p className="today-cards-sub">{next.progress ?? stepLine(next)}</p>
      <button
        className={`btn ${lead ? 'btn-primary' : 'btn-ghost'} btn-lg today-cards-start`}
        onClick={() => {
          play('tap')
          playStep(childId, step.id)
        }}
      >
        <PlayIcon /> {playWords(step)}
      </button>
    </section>
  )
}

/**
 * What she is about to read, so the parent knows before handing it over:
 * the words she will sound out (the sight words are on her path already),
 * the opening line as the book prints it, and how long it is.
 */
function BookInside({ config }: { config: StepStanding['step']['config'] }) {
  const pages = (config.pages ?? []) as Array<{ text: string }>
  const sight = new Set(bookSightWords({ title: String(config.title ?? ''), pages }).map((w) => w.toLowerCase()))
  const names = new Set(((config.names ?? []) as string[]).map((n) => n.toLowerCase()))
  const meet: string[] = []
  for (const p of pages) {
    for (const raw of p.text.split(/[^A-Za-z]+/)) {
      const w = raw.toLowerCase()
      if (w.length > 1 && !sight.has(w) && !names.has(w) && !meet.includes(w)) meet.push(w)
    }
  }
  // A slow decoder takes about half a minute a page, with the cover and the end.
  const minutes = Math.max(2, Math.round(pages.length / 2) + 1)
  return (
    <div className="next-step-book">
      {pages[0] && <p className="next-step-line">{pages[0].text}</p>}
      <p className="next-step-meet">
        <span className="next-step-meet-k">She sounds out</span>
        {meet.slice(0, 5).map((w) => <span key={w} className="path-word">{w}</span>)}
        {meet.length > 5 && <span className="next-step-meet-more">and {meet.length - 5} more</span>}
      </p>
      <p className="next-step-meet-k tnum">{pages.length} pages · about {minutes} minutes</p>
    </div>
  )
}

/** The button says what it plays, so two buttons side by side never read the same. */
function playWords(step: StepStanding['step']): string {
  if (step.track === 'books') return `Read ${step.title}`
  if (step.track === 'blend') return 'Play Blending'
  if (step.track === 'families') return 'Play word families'
  return `Play ${words(step).join(' ')}`
}

/**
 * Blending as she stands on it: the same five-rung ladder as the path, and
 * the words she sounds out on the rung she plays, each with its picture and
 * its sounds (s · u · n), the ones she does not read well yet first. These
 * are the words the game brings her, so the parent sees what she will do.
 */
function BlendInside({ next, memory, now }: { next: StepStanding; memory: Record<string, ItemMemory>; now: number }) {
  const rung = next.rung ?? 1
  const read = (w: string) => holds(memory[itemKey('word', w)], now)
  const meet = WORDS.filter((w) => w.level === rung && pictureSrc(w.word) && !NOT_A_THING.has(w.word))
    .sort((a, b) => Number(read(a.word)) - Number(read(b.word)))
    .slice(0, 4)
  return (
    <div className="next-step-blend">
      <BlendLadder step={next} />
      <p className="next-step-meet-k">She sounds out, at level {rung}</p>
      <PictureTiles label={meet.map((w) => w.word).join(', ')} tiles={meet.map((w) => ({ key: w.word, word: w, big: w.word, small: w.g.join(' · ') }))} />
    </div>
  )
}

/**
 * Tiles across the card, each big text over a clay picture: a sounds set
 * letter by letter (s over the sun, the way the game pairs them), or the
 * words Blending brings her with their sounds under them.
 */
function PictureTiles({ label, tiles }: { label: string; tiles: Array<{ key: string; word?: (typeof WORDS)[number]; big: string; small?: string }> }) {
  return (
    <ul className="next-step-sounds" aria-label={label}>
      {tiles.map(({ key, word, big, small }, i) => {
        const src = word ? pictureSrc(word.word) : null
        return (
          <li key={key} style={{ '--i': i } as React.CSSProperties}>
            <span className="next-step-sound-pic" aria-hidden="true">
              {src ? <img src={src} alt="" /> : <span>{word?.emoji}</span>}
            </span>
            <span className="next-step-sound-letter">{big}</span>
            {small && <span className="next-step-sound-word">{small}</span>}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * A word to picture a letter's sound by: the easiest word in the bank that
 * starts with that letter as its own sound and names a thing with a clay
 * picture, else one with an emoji.
 */
function starter(letter: string) {
  const starts = WORDS.filter((w) => w.g[0] === letter && !NOT_A_THING.has(w.word)).sort((a, b) => a.level - b.level)
  return starts.find((w) => pictureSrc(w.word)) ?? starts[0]
}
/** Bank words that name a doing, a quality or a number (ten is drawn as 10), which would mislabel a letter's sound. */
const NOT_A_THING = new Set(['sad', 'red', 'run', 'hug', 'dig', 'grin', 'swim', 'clap', 'stop', 'drop', 'sing', 'top', 'ten', 'six'])

/** Tracks whose steps are small sets: the hero shows what is in one, big. */
const SETS = new Set(['sight', 'sounds'])

/**
 * The hero is named for the step it plays, as the path names it ("Sounds:
 * s · a · t · m", "Sight words: the · a"), so it reads as that tile on the
 * path and never as a deck or a category that shares a track's name.
 */
function heroTitle(step: StepStanding['step']): string {
  return SETS.has(step.track) ? `${READING_PATH.find((t) => t.id === step.track)?.title}: ${step.title}` : step.title
}
const words = (step: StepStanding['step']) => (step.config.words ?? step.config.letters ?? []) as string[]

/** Before she starts, one plain line on what the step is, not the whole blurb. */
function stepLine({ step }: StepStanding): string {
  if (step.track === 'blend') return 'Joining sounds into words, starting with sounds she can stretch'
  if (step.track === 'sight') return 'A few words to know on sight'
  if (step.track === 'sounds') return 'A few letters and the sounds they make'
  if (step.track === 'books') return 'A little book she reads herself'
  return step.blurb
}

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M8 5.5v13l10.5-6.5z" />
  </svg>
)
